/**
 * Generic interactive command engine.
 *
 * Point/argument-driven commands (2D & 3D alike) share one flow:
 * activate → prompt for 1..N viewport picks (with a rubber-band preview) →
 * commit as a single undoable document change → report to the command line.
 *
 * A handler declares an `InteractionSpec`; the mounted CommandInteractScope
 * collects world-space points on the Z-up ground plane, renders the preview,
 * and calls `onComplete`. Numeric/text results (typed on the command line)
 * fill any prompt step flagged as numeric/text.
 */

import { create } from 'zustand';
import type { ReactNode } from 'react';
import * as THREE from 'three';
import { notify } from '../../utils/notify';
import { useDynamicInputStore } from '../../state/useDynamicInputStore';

export type InteractStepKind = 'point' | 'numeric' | 'text' | 'pick';

/** One clickable command-line option chip (Faz 4.6 — OCS CmdOption).
 *  `keyword` is what the engine receives (typed or clicked); `label` is the
 *  optional display face when it differs from the keyword. */
export interface InteractStepOption {
    keyword: string;
    label?: string;
}

export interface InteractStep {
    /** Command-line prompt shown while this step waits for input. */
    prompt: string;
    kind: InteractStepKind;
    default?: number;
    /**
     * Point step that accumulates indefinitely (2D multi-click loops:
     * polyline-style placement, SKETCH). Enter/finishActive completes it;
     * a minimum number of accumulated points can be required.
     */
    loop?: boolean;
    minPoints?: number;
    /** Pick step: accumulate entity selections (multi-select) until Enter. */
    minPicks?: number;
    /** Pick step: only accept CAD entities of these types (others ignored). */
    acceptTypes?: string[];
    /**
     * Option chips rendered beside the prompt (Faz 4.6). The prompt text no
     * longer embeds the "[a/b/c]" listing — stripOptionListing trims it at
     * the display layer. Picking (or typing) a keyword feeds
     * `provideValue(keyword)` on this step; the runner reads the keyword
     * from its step values — the single keyword path (no text re-parsing).
     */
    options?: InteractStepOption[];
}

export interface InteractionSpec {
    steps: InteractStep[];
    /**
     * When true, pointer-move positions are also collected as points
     * (freehand capture for SKETCH). Completion is triggered by cancel/Esc.
     */
    collectOnMove?: boolean;
    /** Live preview of collected points + current cursor. */
    preview?: (points: THREE.Vector3[], cursor: THREE.Vector3 | null) => ReactNode;
    /**
     * Resolve a picked object: the scope raycasts the scene, finds the
     * entity id, and validates acceptTypes before calling back with it.
     */
    resolvePick?: (entityId: string) => { ok: boolean; reason?: string };
    /**
     * Option-keyword hook (Faz 4.6): invoked with the picked/typed keyword
     * BEFORE the step advances. A runner that wants to branch (CIRCLE 3P →
     * restart as the three-point flow) cancels the active interaction from
     * here and starts its variant spec; when absent, the keyword is stored
     * into the step values and the step is consumed (single-step option
     * picks). Flag-style options that must NOT consume the step should
     * cancel+restart as well, or read the stored keyword in complete().
     */
    onOption?: (keyword: string) => void;
    /**
     * All steps satisfied. `points` = collected point steps in order,
     * `picks` = entity ids picked (in step order; loop steps may hold many),
     * `values` = numeric/text results keyed by step index,
     * `pickPoints` = world click point per pick (same order as `picks`,
     * used by constraint commands to resolve sub-point markers).
     */
    complete: (points: THREE.Vector3[], values: Record<number, number | string>, picks: string[], pickPoints?: THREE.Vector3[]) => string;
    cancel?: () => void;
}

interface CommandInteractState {
    active: boolean;
    spec: InteractionSpec | null;
    stepIndex: number;
    collected: THREE.Vector3[];
    picks: string[];
    /** World-space click location for each pick (Faz 6 constraint refs). */
    pickPoints: THREE.Vector3[];
    values: Record<number, number | string>;
    cursor: THREE.Vector3 | null;
    /** Last completion message, consumed by the command line UI. */
    resultMessage: string | null;

    start: (spec: InteractionSpec) => void;
    setCursor: (p: THREE.Vector3 | null) => void;
    addPoint: (p: THREE.Vector3) => void;
    /** Resolve a viewport pick for the current pick step (scope calls this). */
    addPick: (entityId: string, worldPoint?: THREE.Vector3) => void;
    /** Feed the current step a typed value (numeric/text steps). */
    provideValue: (value: number | string) => void;
    /** Explicit completion (Enter on freehand collectOnMove specs). */
    finishActive: () => string | void;
    cancelActive: () => void;
    consumeResult: () => string | null;
}

function finishCurrent(spec: InteractionSpec, collected: THREE.Vector3[], values: Record<number, number | string>, picks: string[] = [], pickPoints: THREE.Vector3[] = []): string {
    const message = spec.complete(collected, values, picks, pickPoints);
    // Global bridge so every shell's command line (shared commandLog) shows
    // completion results — single writer, no per-shell history copies.
    useDynamicInputStore.getState().appendLog([message, '']);
    useCommandInteract.setState({
        active: false,
        spec: null,
        stepIndex: 0,
        collected: [],
        picks: [],
        pickPoints: [],
        values: {},
        cursor: null,
        resultMessage: message,
    });
    return message;
}

/** Advance past `from` index; complete when the last step is consumed. */
function advanceFrom(s: CommandInteractState, from: number): void {
    const spec = s.spec;
    if (!spec) return;
    const next = from + 1;
    if (next >= spec.steps.length) {
        finishCurrent(spec, s.collected, s.values, s.picks);
    } else {
        useCommandInteract.setState({ stepIndex: next });
    }
}

export const useCommandInteract = create<CommandInteractState>((set, get) => ({
    active: false,
    spec: null,
    stepIndex: 0,
    collected: [],
    picks: [],
    pickPoints: [],
    values: {},
    cursor: null,
    resultMessage: null,

    start: (spec) => set({ active: true, spec, stepIndex: 0, collected: [], picks: [], pickPoints: [], values: {}, cursor: null, resultMessage: null }),
    setCursor: (p) => set({ cursor: p }),

    addPoint: (p) => {
        const s = get();
        if (!s.spec) return;
        const step = s.spec.steps[s.stepIndex];
        if (!step || step.kind !== 'point') return;
        if (s.spec.collectOnMove || step.loop) {
            // Freehand/loop mode: every point just accumulates.
            useCommandInteract.setState({ collected: [...s.collected, p] });
            return;
        }
        useCommandInteract.setState({ collected: [...s.collected, p] });
        advanceFrom(get(), s.stepIndex);
    },

    addPick: (entityId, worldPoint) => {
        const s = get();
        if (!s.spec) return;
        const step = s.spec.steps[s.stepIndex];
        if (!step || step.kind !== 'pick') return;
        useCommandInteract.setState({
            picks: [...s.picks, entityId],
            pickPoints: [...s.pickPoints, worldPoint ?? new THREE.Vector3()],
        });
        advanceFrom(get(), s.stepIndex);
    },

    provideValue: (value) => {
        const s = get();
        if (!s.spec) return;
        const step = s.spec.steps[s.stepIndex];
        if (!step) return;
        // Option keywords (Faz 4.6) are the one value a point/pick step
        // accepts besides real picks: a chip click or a typed keyword lands
        // here and the runner decides — through spec.onOption (branch) or
        // the step values (consume) — the single keyword path into the
        // interaction (no prompt-text re-parsing).
        if (step.kind === 'point' || step.kind === 'pick') {
            const typed = String(value).trim().toUpperCase();
            // OCS keyword semantics: a chip sends the keyword verbatim; a
            // typed keyword matches the full name (case-insensitive) or the
            // capitalized-letter abbreviation ("Ttr" → TT).
            const caps = (kw: string) => kw.replace(/[^A-Z]/g, '').toUpperCase();
            const known = step.options?.some(
                (o) => o.keyword.toUpperCase() === typed || caps(o.keyword) === typed
            );
            if (!known) return;
            if (s.spec.onOption) {
                s.spec.onOption(typed);
                return;
            }
            useCommandInteract.setState({ values: { ...s.values, [s.stepIndex]: typed } });
            advanceFrom(get(), s.stepIndex);
            return;
        }
        useCommandInteract.setState({ values: { ...s.values, [s.stepIndex]: value } });
        advanceFrom(get(), s.stepIndex);
    },

    finishActive: () => {
        const s = get();
        if (!s.spec) return;
        const step = s.spec.steps[s.stepIndex];
        if (step?.loop && step.kind === 'point' && s.collected.length < (step.minPoints ?? 1)) return;
        if (step?.kind === 'pick') {
            // Enter on a pick step: accept accumulated picks (loop) or all.
            const need = step.minPicks ?? 1;
            if (!step.loop) {
                // non-loop pick already advanced; nothing to finish here
            }
            if (s.picks.length < need) return;
            // Jump to completion with what we have: advance through remaining steps.
            return finishCurrent(s.spec, s.collected, s.values, s.picks, s.pickPoints);
        }
        return finishCurrent(s.spec, s.collected, s.values, s.picks, s.pickPoints);
    },

    cancelActive: () => {
        get().spec?.cancel?.();
        set({ active: false, spec: null, stepIndex: 0, collected: [], picks: [], pickPoints: [], values: {}, cursor: null });
    },

    consumeResult: () => {
        const msg = get().resultMessage;
        if (msg !== null) set({ resultMessage: null });
        return msg;
    },
}));

/** Current point/pick prompt for the command line (null when waiting value). */
export function currentPointPrompt(): string | null {
    const s = useCommandInteract.getState();
    if (!s.active || !s.spec) return null;
    const step = s.spec.steps[s.stepIndex];
    return step && (step.kind === 'point' || step.kind === 'pick') ? step.prompt : null;
}

/** Option chips of the active step (empty when the step declares none). */
export function currentStepOptions(): InteractStepOption[] {
    const s = useCommandInteract.getState();
    if (!s.active || !s.spec) return [];
    return s.spec.steps[s.stepIndex]?.options ?? [];
}
